/**
 * 帳號管理 /users（admin；2026-08-21 帳號管理批次）
 * 表格＋篩選＋建立／編輯／停用／重設密碼；臨時密碼只顯示一次。
 * 規格：doc/20260821-客戶反饋/ACCOUNT-SPEC.md §2.3 / §5
 */
import { useState, type ReactNode } from "react";
import { Plus, Pencil, Power, PowerOff, KeyRound, ShieldAlert, Copy, Check } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { fmtDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { FormNotice } from "../../components/account/FormNotice";
import { PasswordFields, newPasswordValid } from "../../components/account/PasswordFields";
import { REGION_LOC, THEAD_ROW } from "./base-data-shared";
import { REGIONS, SUPPORTED_LANGUAGES, type Language, type Loc, type Region, type Role } from "../../../../shared/const";
import { canChangeRole, canManageUser, manageableRoles, canActorChangeRole } from "../../../../shared/account-rules";

type Row = {
  id: number;
  email: string;
  name: string;
  role: Role;
  status: "active" | "inactive" | "suspended";
  isOwner: "yes" | "no";
  mustChangePassword: "yes" | "no";
  language: Language;
  lastSignedInAt: Date | null;
  createdAt: Date;
  companyName: string | null;
  partyRegion: Region | null;
};

const ROLE_LOC: Record<Role, Loc> = {
  admin: { "zh-TW": "管理員", "en-US": "Admin", "zh-CN": "管理员" },
  dispatcher: { "zh-TW": "調度員", "en-US": "Dispatcher", "zh-CN": "调度员" },
  agency: { "zh-TW": "需求方（旅行社）", "en-US": "Agency", "zh-CN": "需求方（旅行社）" },
  fleet: { "zh-TW": "供應方（車隊）", "en-US": "Fleet", "zh-CN": "供应方（车队）" },
};
const ROLE_BADGE: Record<Role, "primary" | "info" | "warm" | "default"> = { admin: "primary", dispatcher: "info", agency: "warm", fleet: "default" };
const STATUS_LOC: Record<Row["status"], Loc> = {
  active: { "zh-TW": "啟用中", "en-US": "Active", "zh-CN": "启用中" },
  inactive: { "zh-TW": "未啟用", "en-US": "Inactive", "zh-CN": "未启用" },
  suspended: { "zh-TW": "已停用", "en-US": "Suspended", "zh-CN": "已停用" },
};
const LANG_NAME: Record<Language, string> = { "zh-TW": "繁體中文", "zh-CN": "简体中文", "en-US": "English" };

export default function Users() {
  const { t, lang } = useLanguage();
  const { user: me } = useAuth();
  // 2026-08-26 權限分層：admin 管所有角色、dispatcher 只管需求方／供應方
  const actorRole: Role = me?.role === "admin" ? "admin" : "dispatcher";
  const allowedRoles = manageableRoles(actorRole);
  const [q, setQ] = useState("");
  const [role, setRole] = useState<"" | Role>("");
  const [status, setStatus] = useState<"" | Row["status"]>("");
  const listQ = trpc.userAdmin.list.useQuery({ q: q || undefined, role: role || undefined, status: status || undefined });
  const rows = (listQ.data ?? []) as Row[];

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [resetting, setResetting] = useState<Row | null>(null);
  const [tempPw, setTempPw] = useState<{ email: string; password: string; mode: "create" | "reset" } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const setStatusM = trpc.userAdmin.setStatus.useMutation({
    onSuccess: () => listQ.refetch(),
    onError: (e) => setActionError(e.message),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "帳號管理", "en-US": "Accounts", "zh-CN": "帐号管理" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": actorRole === "admin"
                ? "建立與維護所有登入帳號（含調度員）；你建立或重設的密碼為臨時密碼，對方首次登入須自行更改。"
                : "建立與維護需求方／供應方帳號；你建立或重設的密碼為臨時密碼，對方首次登入須自行更改。平台方帳號請洽管理員。",
              "en-US": actorRole === "admin"
                ? "Create and maintain all sign-in accounts including dispatchers. Passwords you issue are temporary — users must change them at first sign-in."
                : "Create and maintain agency / fleet accounts. Passwords you issue are temporary. Contact an admin for platform accounts.",
              "zh-CN": actorRole === "admin"
                ? "建立与维护所有登入帐号（含调度员）；你建立或重设的密码为临时密码，对方首次登入须自行更改。"
                : "建立与维护需求方／供应方帐号；你建立或重设的密码为临时密码。平台方帐号请洽管理员。",
            })}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          {t({ "zh-TW": "建立帳號", "en-US": "New account", "zh-CN": "建立帐号" })}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="w-64" placeholder={t({ "zh-TW": "搜尋 Email／姓名／公司", "en-US": "Search email / name / company", "zh-CN": "搜寻 Email／姓名／公司" })} />
        <Select value={role} onChange={(e) => setRole(e.target.value as "" | Role)} className="w-44">
          <option value="">{t({ "zh-TW": "全部角色", "en-US": "All roles", "zh-CN": "全部角色" })}</option>
          {allowedRoles.map((r) => <option key={r} value={r}>{t(ROLE_LOC[r])}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value as "" | Row["status"])} className="w-36">
          <option value="">{t({ "zh-TW": "全部狀態", "en-US": "All statuses", "zh-CN": "全部状态" })}</option>
          {(Object.keys(STATUS_LOC) as Row["status"][]).map((s) => <option key={s} value={s}>{t(STATUS_LOC[s])}</option>)}
        </Select>
        <span className="text-[12px] text-muted-foreground">{rows.length} {t({ "zh-TW": "筆", "en-US": "accounts", "zh-CN": "笔" })}</span>
      </div>

      {actionError && <FormNotice kind="error">{actionError}</FormNotice>}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className="p-3">{t({ "zh-TW": "帳號", "en-US": "Account", "zh-CN": "帐号" })}</th>
              <th className="p-3">{t({ "zh-TW": "角色", "en-US": "Role", "zh-CN": "角色" })}</th>
              <th className="p-3">{t({ "zh-TW": "所屬公司", "en-US": "Company", "zh-CN": "所属公司" })}</th>
              <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
              <th className="p-3">{t({ "zh-TW": "最後登入", "en-US": "Last sign-in", "zh-CN": "最后登入" })}</th>
              <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Actions", "zh-CN": "操作" })}</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t({ "zh-TW": "載入中…", "en-US": "Loading…", "zh-CN": "载入中…" })}</td></tr>
            )}
            {!listQ.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t({ "zh-TW": "沒有符合的帳號。", "en-US": "No accounts match.", "zh-CN": "没有符合的帐号。" })}</td></tr>
            )}
            {rows.map((r) => {
              const isSelf = r.id === me?.id;
              const manageable = canManageUser(actorRole, r.role);
              return (
                <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                  <td className="p-3">
                    <div className="font-medium">{r.name}{isSelf && <span className="ml-1.5 text-[12px] text-muted-foreground">({t({ "zh-TW": "我", "en-US": "you", "zh-CN": "我" })})</span>}</div>
                    <div className="font-mono text-[12px] text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={ROLE_BADGE[r.role]} size="sm">{t(ROLE_LOC[r.role])}</Badge>
                      {r.isOwner === "yes" && <Badge variant="warm" size="sm">owner</Badge>}
                    </div>
                  </td>
                  <td className="p-3">
                    {r.companyName ? (
                      <>
                        <div>{r.companyName}</div>
                        {r.partyRegion && <div className="text-[12px] text-muted-foreground">{t(REGION_LOC[r.partyRegion])}</div>}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={r.status === "active" ? "success" : r.status === "suspended" ? "destructive" : "outline"} size="sm">{t(STATUS_LOC[r.status])}</Badge>
                      {r.mustChangePassword === "yes" && (
                        <Badge variant="warning" size="sm" title={t({ "zh-TW": "尚未用臨時密碼完成首次改密碼", "en-US": "Temporary password not yet changed", "zh-CN": "尚未用临时密码完成首次改密码" })}>
                          <ShieldAlert className="h-2.5 w-2.5" />
                          {t({ "zh-TW": "待改密碼", "en-US": "Temp password", "zh-CN": "待改密码" })}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-[12px] text-muted-foreground">{fmtDateTime(r.lastSignedInAt, lang)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" disabled={!manageable} onClick={() => setEditing(r)} title={t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setResetting(r)} disabled={!manageable || (r.isOwner === "yes" && !isSelf)} title={t({ "zh-TW": "重設密碼", "en-US": "Reset password", "zh-CN": "重设密码" })}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      {r.status === "active" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!manageable || r.isOwner === "yes" || isSelf || setStatusM.isPending}
                          onClick={() => { setActionError(null); setStatusM.mutate({ id: r.id, status: "suspended" }); }}
                          title={t({ "zh-TW": "停用", "en-US": "Suspend", "zh-CN": "停用" })}
                        >
                          <PowerOff className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!manageable || setStatusM.isPending}
                          onClick={() => { setActionError(null); setStatusM.mutate({ id: r.id, status: "active" }); }}
                          title={t({ "zh-TW": "啟用", "en-US": "Activate", "zh-CN": "启用" })}
                        >
                          <Power className="h-3.5 w-3.5 text-[var(--color-success)]" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateDialog
        actorRole={actorRole}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(email, temporaryPassword) => {
          void listQ.refetch();
          if (temporaryPassword) setTempPw({ email, password: temporaryPassword, mode: "create" });
        }}
      />
      <EditDialog actorRole={actorRole} row={editing} selfId={me?.id ?? 0} onOpenChange={(o) => !o && setEditing(null)} onSaved={() => listQ.refetch()} />
      <ResetDialog
        row={resetting}
        onOpenChange={(o) => !o && setResetting(null)}
        onReset={(email, password) => {
          void listQ.refetch();
          setTempPw({ email, password, mode: "reset" });
        }}
      />
      <TempPasswordDialog data={tempPw} onClose={() => setTempPw(null)} />
    </div>
  );
}

/* ───────────── 建立帳號 ───────────── */
function CreateDialog({ actorRole, open, onOpenChange, onCreated }: { actorRole: Role; open: boolean; onOpenChange: (o: boolean) => void; onCreated: (email: string, temporaryPassword: string | null) => void }) {
  const { t } = useLanguage();
  const roleOptions = manageableRoles(actorRole);
  const blank = { email: "", name: "", role: (actorRole === "admin" ? "dispatcher" : "agency") as Role, language: "zh-TW" as Language, companyName: "", region: "taiwan" as Region, contactPerson: "", phone: "" };
  const [f, setF] = useState(blank);
  const [autoPw, setAutoPw] = useState(true);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof blank>(k: K, v: (typeof blank)[K]) => setF((s) => ({ ...s, [k]: v }));
  const isParty = f.role === "agency" || f.role === "fleet";

  const m = trpc.userAdmin.create.useMutation({
    onSuccess: (r) => {
      onCreated(f.email.trim(), r.temporaryPassword);
      onOpenChange(false);
      setF(blank); setPw(""); setConfirm(""); setAutoPw(true);
    },
    onError: (e) => setError(e.message),
  });

  const valid =
    f.email.trim() !== "" && f.name.trim() !== "" &&
    (!isParty || (f.companyName.trim() !== "")) &&
    (autoPw || newPasswordValid(pw, confirm));

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setError(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "建立帳號", "en-US": "New account", "zh-CN": "建立帐号" })}</DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "建立後對方首次登入須自行更改密碼。", "en-US": "The user must change the password at first sign-in.", "zh-CN": "建立后对方首次登入须自行更改密码。" })}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            m.mutate({
              email: f.email.trim(),
              name: f.name.trim(),
              role: f.role,
              language: f.language,
              ...(autoPw ? {} : { password: pw }),
              ...(isParty ? { companyName: f.companyName.trim(), region: f.region, contactPerson: f.contactPerson.trim() || undefined, phone: f.phone.trim() || undefined } : {}),
            });
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t({ "zh-TW": "角色", "en-US": "Role", "zh-CN": "角色" })}>
              <Select value={f.role} onChange={(e) => set("role", e.target.value as Role)}>
                {roleOptions.map((r) => <option key={r} value={r}>{t(ROLE_LOC[r])}</option>)}
              </Select>
            </Field>
            <Field label={t({ "zh-TW": "介面語言", "en-US": "Language", "zh-CN": "介面语言" })}>
              <Select value={f.language} onChange={(e) => set("language", e.target.value as Language)}>
                {SUPPORTED_LANGUAGES.map((l) => <option key={l} value={l}>{LANG_NAME[l]}</option>)}
              </Select>
            </Field>
            <Field label="Email *"><Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label={`${t({ "zh-TW": "姓名", "en-US": "Name", "zh-CN": "姓名" })} *`}><Input required value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          </div>

          {isParty && (
            <div className="grid grid-cols-1 gap-3 rounded-md border border-border bg-muted/40 p-3 sm:grid-cols-2">
              <Field label={`${t({ "zh-TW": "公司名稱", "en-US": "Company", "zh-CN": "公司名称" })} *`}><Input required value={f.companyName} onChange={(e) => set("companyName", e.target.value)} /></Field>
              <Field label={`${t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })} *`}>
                <Select value={f.region} onChange={(e) => set("region", e.target.value as Region)}>
                  {REGIONS.map((r) => <option key={r} value={r}>{t(REGION_LOC[r])}</option>)}
                </Select>
              </Field>
              <Field label={t({ "zh-TW": "聯絡人", "en-US": "Contact person", "zh-CN": "联络人" })}><Input value={f.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} /></Field>
              <Field label={t({ "zh-TW": "聯絡電話", "en-US": "Phone", "zh-CN": "联络电话" })}><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" className="accent-[var(--color-primary)]" checked={autoPw} onChange={(e) => setAutoPw(e.target.checked)} />
              {t({ "zh-TW": "由系統產生臨時密碼（建立後顯示一次）", "en-US": "Generate a temporary password (shown once)", "zh-CN": "由系统产生临时密码（建立后显示一次）" })}
            </label>
            {!autoPw && <PasswordFields password={pw} confirm={confirm} onPassword={setPw} onConfirm={setConfirm} idPrefix="cu" />}
          </div>

          {error && <FormNotice kind="error">{error}</FormNotice>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
            <Button type="submit" disabled={!valid || m.isPending}>
              {m.isPending ? t({ "zh-TW": "建立中…", "en-US": "Creating…", "zh-CN": "建立中…" }) : t({ "zh-TW": "建立", "en-US": "Create", "zh-CN": "建立" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── 編輯 ───────────── */
function EditDialog({ actorRole, row, selfId, onOpenChange, onSaved }: { actorRole: Role; row: Row | null; selfId: number; onOpenChange: (o: boolean) => void; onSaved: () => void }) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [language, setLanguage] = useState<Language>("zh-TW");
  const [role, setRole] = useState<Role>("dispatcher");
  const [error, setError] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  if (row && loadedFor !== row.id) {
    setLoadedFor(row.id); setName(row.name); setLanguage(row.language); setRole(row.role); setError(null);
  }
  const m = trpc.userAdmin.update.useMutation({
    onSuccess: () => { onSaved(); onOpenChange(false); },
    onError: (e) => setError(e.message),
  });
  if (!row) return null;
  const roleLocked = !canActorChangeRole(actorRole) || !canChangeRole(row.role, "admin") || row.isOwner === "yes" || row.id === selfId;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "編輯帳號", "en-US": "Edit account", "zh-CN": "编辑帐号" })}</DialogTitle>
          <DialogDescription className="font-mono">{row.email}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            m.mutate({ id: row.id, name: name.trim(), language, ...(role !== row.role ? { role } : {}) });
          }}
        >
          <Field label={`${t({ "zh-TW": "姓名", "en-US": "Name", "zh-CN": "姓名" })} *`}><Input required value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label={t({ "zh-TW": "介面語言", "en-US": "Language", "zh-CN": "介面语言" })}>
            <Select value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
              {SUPPORTED_LANGUAGES.map((l) => <option key={l} value={l}>{LANG_NAME[l]}</option>)}
            </Select>
          </Field>
          <Field label={t({ "zh-TW": "角色", "en-US": "Role", "zh-CN": "角色" })}>
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={roleLocked}>
              {roleLocked ? <option value={row.role}>{t(ROLE_LOC[row.role])}</option> : (["admin", "dispatcher"] as Role[]).map((r) => <option key={r} value={r}>{t(ROLE_LOC[r])}</option>)}
            </Select>
            <p className="text-[12px] text-muted-foreground">
              {row.role === "agency" || row.role === "fleet"
                ? t({ "zh-TW": "需求方／供應方綁定公司資料，角色不可變更。", "en-US": "Agency / fleet roles are tied to a company and cannot change.", "zh-CN": "需求方／供应方绑定公司资料，角色不可变更。" })
                : roleLocked
                  ? t({ "zh-TW": "不可變更擁有者或自己的角色。", "en-US": "The owner's or your own role cannot change.", "zh-CN": "不可变更拥有者或自己的角色。" })
                  : t({ "zh-TW": "僅管理員與調度員可互換。", "en-US": "Only admin ↔ dispatcher.", "zh-CN": "仅管理员与调度员可互换。" })}
            </p>
          </Field>
          {error && <FormNotice kind="error">{error}</FormNotice>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
            <Button type="submit" disabled={name.trim() === "" || m.isPending}>{t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "储存" })}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── 重設密碼（確認） ───────────── */
function ResetDialog({ row, onOpenChange, onReset }: { row: Row | null; onOpenChange: (o: boolean) => void; onReset: (email: string, password: string) => void }) {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const m = trpc.userAdmin.resetPassword.useMutation({
    onSuccess: (r) => { if (row) onReset(row.email, r.temporaryPassword); onOpenChange(false); },
    onError: (e) => setError(e.message),
  });
  if (!row) return null;
  return (
    <Dialog open onOpenChange={(o) => { onOpenChange(o); if (!o) setError(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "重設密碼", "en-US": "Reset password", "zh-CN": "重设密码" })}</DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": `將為 ${row.email} 產生一組新的臨時密碼；對方所有裝置的登入會立即失效，首次登入須自行更改密碼。`,
              "en-US": `A new temporary password will be generated for ${row.email}. All their sessions are signed out and they must change it at first sign-in.`,
              "zh-CN": `将为 ${row.email} 产生一组新的临时密码；对方所有装置的登入会立即失效，首次登入须自行更改密码。`,
            })}
          </DialogDescription>
        </DialogHeader>
        {error && <FormNotice kind="error">{error}</FormNotice>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
          <Button type="button" variant="destructive" disabled={m.isPending} onClick={() => { setError(null); m.mutate({ id: row.id }); }}>
            <KeyRound className="h-3.5 w-3.5" />
            {m.isPending ? t({ "zh-TW": "重設中…", "en-US": "Resetting…", "zh-CN": "重设中…" }) : t({ "zh-TW": "確認重設", "en-US": "Reset", "zh-CN": "确认重设" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── 臨時密碼（只顯示一次） ───────────── */
function TempPasswordDialog({ data, onClose }: { data: { email: string; password: string; mode: "create" | "reset" } | null; onClose: () => void }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.password); setCopied(true); window.setTimeout(() => setCopied(false), 2000); } catch { /* 剪貼簿不可用時使用者仍可手動選取 */ }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {data.mode === "create"
              ? t({ "zh-TW": "帳號已建立", "en-US": "Account created", "zh-CN": "帐号已建立" })
              : t({ "zh-TW": "密碼已重設", "en-US": "Password reset", "zh-CN": "密码已重设" })}
          </DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "臨時密碼只顯示這一次，關閉後無法再查看。請透過安全管道交給對方。",
              "en-US": "This temporary password is shown only once. Share it with the user through a secure channel.",
              "zh-CN": "临时密码只显示这一次，关闭后无法再查看。请透过安全管道交给对方。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 rounded-md border border-border bg-muted/40 p-4">
          <div className="text-[12px] text-muted-foreground">Email</div>
          <div className="font-mono text-[13px]">{data.email}</div>
          <div className="pt-2 text-[12px] text-muted-foreground">{t({ "zh-TW": "臨時密碼", "en-US": "Temporary password", "zh-CN": "临时密码" })}</div>
          <div className="flex items-center gap-2">
            <code className="select-all rounded bg-background px-2 py-1 font-mono text-[15px] tracking-wider">{data.password}</code>
            <Button type="button" variant="outline" size="sm" onClick={copy}>
              {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t({ "zh-TW": "已複製", "en-US": "Copied", "zh-CN": "已复制" }) : t({ "zh-TW": "複製", "en-US": "Copy", "zh-CN": "复制" })}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>{t({ "zh-TW": "我已記下，關閉", "en-US": "Done", "zh-CN": "我已记下，关闭" })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      {children}
    </div>
  );
}
